/**
 * Dexie (IndexedDB) schema for the LCDD Catalogue PWA.
 *
 * Tables:
 *   items        — all FP product rows from /api/itemtbl/
 *   categories5  — category 5 descriptions
 *   companies    — company list (for price customer selector)
 *   settings     — catalogue branding settings (single row: id='main')
 *   syncMeta     — tracks last-sync timestamp (single row: id='main')
 *   itemTiers    — airline tier per item (itemid -> tier) when Airline Mode is on
 *   assetBlobs   — uploaded logo/banner blobs (id: 'logo' | 'banner')
 *   brandingProfiles — saved branding profiles (name + settings snapshot)
 */

import Dexie, { type Table } from 'dexie';
import type { ItemRow, Category5Row, CompanyRow, CatalogueSettings, SyncMeta, AirlineTier } from '../types';
import { DEFAULT_CATALOGUE_SETTINGS } from '../types';

export type ItemTierRow = { itemid: number; tier: AirlineTier };

/**
 * Per-user personalization of a catalogue item, stored LOCALLY only (never sent
 * to the server — the master record lives in Trac-IT on-prem). Survives catalogue
 * syncs because it lives in its own table (the synced `items` table is replaced
 * wholesale on every sync). Merged over the master item at display/PDF/portfolio
 * time. A key set to `undefined`/absent = "use the master value".
 */
export type ItemOverrideRow = {
  itemid: number;
  item_marketingproducttitle?: string | null;
  item_marketingdescription?: string | null;
  item_webprice?: number | null;
};

const OVERRIDE_KEYS = [
  'item_marketingproducttitle',
  'item_marketingdescription',
  'item_webprice',
] as const;

/**
 * Per-user personalization of the catalogue branding settings (brand name,
 * subtitle, logo/banner file, colours), stored LOCALLY only — same rationale as
 * ItemOverrideRow. The synced `settings` table is replaced on every sync; this
 * override survives and is merged on top, so a rep's branding sticks.
 */
export type SettingsOverrideRow = Partial<CatalogueSettings> & { id: string };

export type AssetBlobRow = { id: string; blob: Blob; fileName?: string };

export type BrandingProfileRow = {
  id: string;
  name: string;
  createdAt: number;
  settings: CatalogueSettings;
};

export type SavedLookbookRow = {
  id: string;
  name: string;
  createdAt: number;
  itemIds: number[];
  /** Full item snapshots embedded for offline use */
  items: ItemRow[];
  showIngredients: boolean;
  airlineMode: boolean;
  showPrices: boolean;
  showDefrostLife?: boolean;
  showHandling?: boolean;
  tierEntries: [number, string][];
};

class CatalogueDatabase extends Dexie {
  items!: Table<ItemRow, number>;
  categories5!: Table<Category5Row, number>;
  companies!: Table<CompanyRow, number>;
  settings!: Table<CatalogueSettings & { id: 'main' }, string>;
  syncMeta!: Table<SyncMeta, string>;
  itemTiers!: Table<ItemTierRow, number>;
  itemOverrides!: Table<ItemOverrideRow, number>;
  settingsOverride!: Table<SettingsOverrideRow, string>;
  assetBlobs!: Table<AssetBlobRow, string>;
  brandingProfiles!: Table<BrandingProfileRow, string>;
  savedLookbooks!: Table<SavedLookbookRow, string>;

  constructor() {
    super('lcdd-catalogue-v3');

    this.version(1).stores({
      items: 'itemid, itemcategory, itemislive, category5id_fk, itemcode',
      categories5: 'category5id',
      companies: 'companyid',
      settings: 'id',
      syncMeta: 'id',
    });

    this.version(2).stores({
      items: 'itemid, itemcategory, itemislive, category5id_fk, itemcode',
      categories5: 'category5id',
      companies: 'companyid',
      settings: 'id',
      syncMeta: 'id',
      itemTiers: 'itemid',
    });

    this.version(3).stores({
      items: 'itemid, itemcategory, itemislive, category5id_fk, itemcode',
      categories5: 'category5id',
      companies: 'companyid',
      settings: 'id',
      syncMeta: 'id',
      itemTiers: 'itemid',
      assetBlobs: 'id',
      brandingProfiles: 'id',
    });

    this.version(4).stores({
      items: 'itemid, itemcategory, itemislive, category5id_fk, itemcode',
      categories5: 'category5id',
      companies: 'companyid',
      settings: 'id',
      syncMeta: 'id',
      itemTiers: 'itemid',
      assetBlobs: 'id',
      brandingProfiles: 'id',
      savedLookbooks: 'id',
    });

    this.version(5).stores({
      items: 'itemid, itemcategory, itemislive, category5id_fk, itemcode',
      categories5: 'category5id',
      companies: 'companyid',
      settings: 'id',
      syncMeta: 'id',
      itemTiers: 'itemid',
      itemOverrides: 'itemid',
      assetBlobs: 'id',
      brandingProfiles: 'id',
      savedLookbooks: 'id',
    });

    this.version(6).stores({
      items: 'itemid, itemcategory, itemislive, category5id_fk, itemcode',
      categories5: 'category5id',
      companies: 'companyid',
      settings: 'id',
      syncMeta: 'id',
      itemTiers: 'itemid',
      itemOverrides: 'itemid',
      settingsOverride: 'id',
      assetBlobs: 'id',
      brandingProfiles: 'id',
      savedLookbooks: 'id',
    });
  }
}

export const db = new CatalogueDatabase();

/* ------------------------------------------------------------------ */
/*  Personalization helpers (local-only item overrides)               */
/* ------------------------------------------------------------------ */

/** Load all local item overrides into a Map keyed by itemid. */
export async function loadOverridesMap(): Promise<Map<number, ItemOverrideRow>> {
  const rows = await db.itemOverrides.toArray();
  return new Map(rows.map(r => [r.itemid, r]));
}

/** Whether an override row actually personalizes at least one field. */
export function hasOverride(ov?: ItemOverrideRow | null): boolean {
  if (!ov) return false;
  return OVERRIDE_KEYS.some(k => ov[k] !== undefined);
}

/** Merge a single override onto a master item (override wins for defined keys). */
export function applyOverride(item: ItemRow, ov?: ItemOverrideRow | null): ItemRow {
  if (!hasOverride(ov)) return item;
  const merged = { ...item } as ItemRow;
  for (const k of OVERRIDE_KEYS) {
    if (ov![k] !== undefined) {
      (merged as Record<string, unknown>)[k] = ov![k];
    }
  }
  return merged;
}

/** Merge a list of master items with the override map. */
export function mergeOverrides(items: ItemRow[], map: Map<number, ItemOverrideRow>): ItemRow[] {
  if (map.size === 0) return items;
  return items.map(it => applyOverride(it, map.get(it.itemid)));
}

/* ------------------------------------------------------------------ */
/*  Catalogue settings personalization (local-only branding override)  */
/* ------------------------------------------------------------------ */

/** Merge order: built-in defaults < synced settings < local override. */
export function mergeSettings(
  synced: Partial<CatalogueSettings> | null | undefined,
  override: Partial<CatalogueSettings> | null | undefined,
): CatalogueSettings {
  return {
    ...DEFAULT_CATALOGUE_SETTINGS,
    ...(synced ?? {}),
    ...(override ?? {}),
    colours: {
      ...DEFAULT_CATALOGUE_SETTINGS.colours,
      ...(synced?.colours ?? {}),
      ...(override?.colours ?? {}),
    },
  };
}

/** The user's local branding override, or null if they haven't personalized. */
export async function loadSettingsOverride(): Promise<SettingsOverrideRow | null> {
  return (await db.settingsOverride.get('main')) ?? null;
}

/** Whether a local branding override exists. */
export async function hasSettingsOverride(): Promise<boolean> {
  return (await db.settingsOverride.get('main')) != null;
}

/** Persist the user's branding personalization (replaces the whole override row). */
export async function saveSettingsOverride(patch: Partial<CatalogueSettings>): Promise<void> {
  await db.settingsOverride.put({ id: 'main', ...patch });
}

/** Clear the local branding override (revert to the synced catalogue settings). */
export async function clearSettingsOverride(): Promise<void> {
  await db.settingsOverride.delete('main');
}

/** Effective settings: synced catalogue settings with the local override applied. */
export async function loadEffectiveSettings(): Promise<CatalogueSettings> {
  const [synced, override] = await Promise.all([
    db.settings.get('main'),
    db.settingsOverride.get('main'),
  ]);
  return mergeSettings(synced, override);
}
